import { useEffect, useState } from "react";
import { Box, InputBase } from "@mui/material";
import {
  NotificationsActive as BellIcon,
  Close as CloseIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Smartphone as PhoneIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import { sendPushToTrainees } from "../../../services/AdminMessageService";

const TEMPLATES = [
  { key: "workout", btn: "💪 תזכורת אימון", title: "תזכורת לאימון 💪", body: "היי! יש לך אימון מתוכנן היום. בוא נשבור שיאים! 🔥" },
  { key: "checkin", btn: "📋 צ׳ק-אין", title: "זמן לצ׳ק-אין 📋", body: "שלום! שבוע חדש התחיל. מלא את הצ׳ק-אין השבועי שלך ועדכן אותי בהתקדמות." },
  { key: "renewal", btn: "🔄 חידוש מנוי", title: "חידוש מנוי 🔄", body: "המנוי שלך מסתיים בקרוב. צור קשר לחידוש ולהמשך ההתקדמות שלנו יחד!" },
  { key: "progress", btn: "🎉 כל הכבוד", title: "כל הכבוד! 🎉", body: "השגת את היעד שלך השבוע! מרוצה ממך — ממשיכים להתקדם!" },
];

const Label = ({ icon, children }) => (
  <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: t.t2, display: "flex", alignItems: "center", gap: "4px", textTransform: "uppercase", letterSpacing: ".05em" }}>
    {icon}
    {children}
  </Box>
);

const inputSx = {
  padding: "7px 11px",
  border: `1.5px solid ${t.border}`,
  borderRadius: t.rs,
  background: t.bg,
  color: t.text,
  fontSize: "13px",
  "& input, & textarea": { padding: 0 },
  "&:focus-within": { borderColor: t.green, background: t.white, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" },
};

const PushComposer = ({ selectedIds, idToName, onClear, onSent }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTick, setSentTick] = useState(false);

  useEffect(() => {
    if (selectedIds.size === 0) {
      setSentTick(false);
    }
  }, [selectedIds.size]);

  const hasRecipients = selectedIds.size > 0;

  const applyTemplate = (k) => {
    const tpl = TEMPLATES.find((x) => x.key === k);
    if (!tpl) return;
    setTitle(tpl.title);
    setBody(tpl.body);
  };

  const send = async () => {
    if (!body.trim() || sending || !hasRecipients) return;
    setSending(true);
    try {
      await sendPushToTrainees(Array.from(selectedIds), title.trim() || null, body.trim());
      setSentTick(true);
      setTitle("");
      setBody("");
      setTimeout(() => {
        onSent?.(selectedIds.size);
        setSentTick(false);
      }, 800);
    } catch (e) {
      setSending(false);
      onSent?.(0, e);
      return;
    }
    setSending(false);
  };

  return (
    <Box
      sx={{
        background: t.white,
        border: `1px solid ${t.border}`,
        borderRadius: t.r,
        boxShadow: t.sh,
        marginTop: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "13px 18px",
          borderBottom: `1px solid ${t.border}`,
          background: `linear-gradient(135deg, ${t.greenLight}, #f4fbf8)`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Box sx={{ fontSize: "13px", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px", color: t.greenText }}>
            <BellIcon sx={{ fontSize: 16, color: t.green }} />
            שליחת הודעת פוש
          </Box>
          <Box sx={{ fontSize: "11px", color: t.t2 }}>
            {hasRecipients
              ? "כתבו הודעה ושלחו פוש למתאמנים שנבחרו"
              : "סמנו מתאמנים בטבלה למעלה כדי לשלוח להם הודעת פוש"}
          </Box>
        </Box>
        {hasRecipients && (
          <Box
            component="button"
            onClick={onClear}
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              border: `1px solid ${t.border}`,
              background: t.white,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: t.t2,
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 220px" } }}>
        <Box sx={{ padding: "14px 16px", borderLeft: { md: `1px solid ${t.border}` }, display: "flex", flexDirection: "column", gap: "10px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Label icon={<PeopleIcon sx={{ fontSize: 12 }} />}>נמענים</Label>
            {hasRecipients ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {[...selectedIds].map((id) => (
                  <Box
                    key={id}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      background: t.greenLight,
                      color: t.greenText,
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "3px 9px",
                      borderRadius: "20px",
                      border: "1px solid rgba(1,187,167,.2)",
                    }}
                  >
                    {(idToName.get(id) || "").split(" ")[0] || id}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ fontSize: "11.5px", color: t.t3, padding: "6px 0" }}>
                לא נבחרו מתאמנים — בחר מתאמנים מהטבלה כדי לשלוח להם פוש
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Label>כותרת ההודעה</Label>
            <InputBase
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 50))}
              placeholder="למשל: תזכורת לאימון 💪"
              sx={inputSx}
            />
            <Box sx={{ fontSize: "10.5px", color: title.length > 45 ? t.red : t.t3, textAlign: "left" }}>{title.length}/50</Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Label>גוף ההודעה</Label>
            <InputBase
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 120))}
              placeholder="כתוב הודעה אישית למתאמנים..."
              multiline
              minRows={2}
              sx={{ ...inputSx, height: "auto" }}
            />
            <Box sx={{ fontSize: "10.5px", color: body.length > 110 ? t.red : t.t3, textAlign: "left" }}>{body.length}/120</Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Label>תבניות מהירות</Label>
            <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
              {TEMPLATES.map((tpl) => (
                <Box
                  key={tpl.key}
                  component="button"
                  onClick={() => applyTemplate(tpl.key)}
                  sx={{
                    padding: "4px 10px",
                    borderRadius: "20px",
                    border: `1px solid ${t.border}`,
                    background: t.bg,
                    color: t.t2,
                    fontSize: "11px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.greenText },
                  }}
                >
                  {tpl.btn}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ padding: "14px", background: t.bg, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Label icon={<PhoneIcon sx={{ fontSize: 12 }} />}>תצוגה מקדימה</Label>
          </Box>
          <Box
            sx={{
              width: 160,
              background: "#1c1c2e",
              borderRadius: "24px",
              padding: "10px 8px 14px",
              boxShadow: "0 12px 40px rgba(0,0,0,.3)",
              marginTop: "8px",
            }}
          >
            <Box sx={{ width: 50, height: 6, background: "#2a2a3e", borderRadius: "3px", margin: "0 auto 10px" }} />
            <Box sx={{ background: "#f5f5f0", borderRadius: "14px", overflow: "hidden", minHeight: 100 }}>
              <Box sx={{ background: "rgba(255,255,255,.96)", margin: "6px", borderRadius: "12px", padding: "10px", boxShadow: "0 2px 10px rgba(0,0,0,.12)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "5px" }}>
                  <Box sx={{ fontSize: 12 }}>💪</Box>
                  <Box sx={{ fontSize: "10px", fontWeight: 700, color: "#333", flex: 1 }}>פרוגרס</Box>
                  <Box sx={{ fontSize: "9px", color: "#999" }}>עכשיו</Box>
                </Box>
                <Box sx={{ fontSize: "11px", fontWeight: 700, color: "#111", marginBottom: "3px", lineHeight: 1.3 }}>
                  {title || "כותרת ההודעה"}
                </Box>
                <Box sx={{ fontSize: "10.5px", color: "#555", lineHeight: 1.35 }}>
                  {body || "גוף ההודעה יופיע כאן..."}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "11px 16px", borderTop: `1px solid ${t.border}`, background: t.bg }}>
        <Box sx={{ fontSize: "12px", color: t.t2, display: "flex", alignItems: "center", gap: "5px", fontWeight: 500 }}>
          <PeopleIcon sx={{ fontSize: 14 }} />
          {selectedIds.size} נמענים נבחרו
        </Box>
        <Box sx={{ flex: 1 }} />
        {hasRecipients && (
          <Box
            component="button"
            onClick={onClear}
            sx={{ padding: "8px 12px", borderRadius: t.rs, background: "transparent", color: t.t2, border: `1.5px solid ${t.border}`, fontSize: "12px", cursor: "pointer", "&:hover": { background: t.bg, color: t.text } }}
          >
            בטל
          </Box>
        )}
        <Box
          component="button"
          onClick={send}
          disabled={!body.trim() || sending || !hasRecipients}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "8px 15px",
            borderRadius: t.rs,
            background: sentTick ? t.greenDark : t.green,
            color: "#fff",
            border: "none",
            fontSize: "12.5px",
            fontWeight: 600,
            cursor: !body.trim() || sending || !hasRecipients ? "not-allowed" : "pointer",
            opacity: !body.trim() || !hasRecipients ? 0.6 : 1,
            "&:hover": !hasRecipients ? {} : { background: t.greenDark },
          }}
        >
          {sentTick ? <CheckIcon sx={{ fontSize: 14 }} /> : <SendIcon sx={{ fontSize: 14 }} />}
          {sentTick ? "נשלח!" : "שלח פוש"}
        </Box>
      </Box>
    </Box>
  );
};

export default PushComposer;
